import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowRight, AlertTriangle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useCompanyInfo } from "@/hooks/useCompanyInfo";
import { supabase, syncTokenFromSession } from "@/lib/supabaseClient";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO = "/dr-rumble-tumble.png";
const BRAND_NAME = "Dr Rumble Tumble";
const BRAND_TAGLINE = "Hotel Linen System";

export default function Login({ onLogin }) {
  const { companyInfo } = useCompanyInfo();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // Maintenance state — polled once on mount; if `mode` is true, hotel users
  // can't log in (admin/manager still pass through, enforced server-side too).
  const [maintenance, setMaintenance] = useState({ mode: false, message: "" });
  // Terms & Conditions — must be ticked manually before signing in; content
  // is editable from Settings → Company Info and viewable via the dialog.
  const [agreed, setAgreed] = useState(false);
  const [terms, setTerms] = useState("");
  const [termsOpen, setTermsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const resp = await axios.get(`${API}/maintenance-status`, { timeout: 5000 });
        if (!cancelled) {
          setMaintenance({
            mode: !!resp.data?.maintenance_mode,
            message: resp.data?.maintenance_message || "",
          });
        }
      } catch (_) { /* silently ignore — login still works */ }
    };
    fetchStatus();
    axios.get(`${API}/terms`, { timeout: 5000 })
      .then((r) => { if (!cancelled) setTerms(r.data?.content || ""); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!agreed) {
      toast.error("Please accept the Terms & Conditions to sign in");
      return;
    }
    setLoading(true);
    try {
      const trimmedEmail = email.trim();
      if (!trimmedEmail || !trimmedEmail.includes("@")) {
        toast.error("Please enter a valid email address");
        setLoading(false);
        return;
      }
      if (!password) {
        toast.error("Please enter your password");
        setLoading(false);
        return;
      }

      // Clear any stale session BEFORE signing in. supabase-js v2 can deadlock
      // ("Body is disturbed or locked" / "Response body is already used") when
      // a background token refresh collides with signInWithPassword. We also
      // wrap with a hard timeout so the request can never hang forever.
      try {
        await Promise.race([
          supabase.auth.signOut({ scope: "local" }),
          new Promise((_, rej) => setTimeout(() => rej(new Error("signout-timeout")), 4000)),
        ]);
      } catch (_) { /* best effort */ }

      // Manual timeout wrapper around any supabase-js auth call. The lib has no
      // built-in timeout and will keep a hung XHR pending indefinitely.
      const withTimeout = (p, ms, label) =>
        Promise.race([
          p,
          new Promise((_, rej) =>
            setTimeout(() => rej(new Error(`AUTH_TIMEOUT:${label}`)), ms)
          ),
        ]);

      let signInResult;
      // Up to 3 attempts to dodge Supabase's internal LockAcquireTimeoutError
      // race condition (happens when the recovered-session refresh collides
      // with our explicit signInWithPassword). After the 3rd we surface the
      // last error to the outer catch.
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          signInResult = await withTimeout(
            supabase.auth.signInWithPassword({
              email: trimmedEmail,
              password,
            }),
            15000,
            "signin"
          );
          break;
        } catch (sbErr) {
          const m = String(sbErr?.message || "").toLowerCase();
          const isTimeout = m.includes("auth_timeout");
          const isRace = m.includes("disturbed") || m.includes("locked") || m.includes("clone") || m.includes("already used");
          if ((!isRace && !isTimeout) || attempt === 2) {
            throw sbErr;
          }
          // Wait and clear local session, then retry.
          await new Promise((r) => setTimeout(r, 350 * (attempt + 1)));
          try {
            await Promise.race([
              supabase.auth.signOut({ scope: "local" }),
              new Promise((res) => setTimeout(res, 2000)),
            ]);
          } catch (_) {}
        }
      }
      const { data, error } = signInResult;
      if (error) {
        const m = (error.message || "").toLowerCase();
        if (m.includes("invalid login credentials") || m.includes("invalid_credentials")) {
          // Bilingual message — surfaced via the toast catch handler below.
          throw new Error("PASSWORD_INCORRECT");
        }
        throw error;
      }

      syncTokenFromSession(data.session);

      // Use axios (XHR) instead of fetch to avoid Emergent rrweb-recorder
      // overriding window.fetch and double-cloning the Response body.
      const meResp = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${data.session.access_token}` },
        validateStatus: () => true,
      });
      if (meResp.status === 503) {
        // Maintenance mode — backend rejected the customer. Sign them back out
        // and show a friendly message on the login page.
        try { await supabase.auth.signOut({ scope: "local" }); } catch (_) {}
        const detail = meResp.data?.detail || "The system is temporarily unavailable for maintenance.";
        setMaintenance({ mode: true, message: detail });
        toast.error("Login disabled — system maintenance in progress");
        return;
      }
      if (meResp.status < 200 || meResp.status >= 300) {
        throw new Error(`Failed to load profile: ${typeof meResp.data === "string" ? meResp.data : JSON.stringify(meResp.data)}`);
      }
      const profile = meResp.data;
      // Drivers must NOT enter the full-mode app. Redirect them to the PDA
      // login page with a friendly toast and clear their partial session.
      if (profile?.role === "driver") {
        try { await supabase.auth.signOut({ scope: "local" }); } catch (_) {}
        toast.info("Driver accounts use the PDA app. Redirecting…");
        setTimeout(() => { window.location.href = "/pda/login"; }, 800);
        return;
      }
      localStorage.setItem("user", JSON.stringify(profile));

      toast.success("Login successful");
      onLogin(profile, data.session.access_token);
    } catch (error) {
      const msg = String(error?.message || "");
      const lower = msg.toLowerCase();
      if (msg === "PASSWORD_INCORRECT") {
        toast.error("Password incorrect", {
          description: "Forgot your password? Please contact your administrator.",
          duration: 8000,
        });
      } else if (lower.includes("auth_timeout")) {
        // Hung supabase-js call — clear local session + tell user how to recover.
        try { await supabase.auth.signOut({ scope: "local" }); } catch (_) {}
        try { localStorage.removeItem("token"); localStorage.removeItem("user"); } catch (_) {}
        toast.error("Sign-in took too long", {
          description: "Click Sign in again. If it keeps happening, open DevTools → Application → Clear site data, then refresh.",
          duration: 10000,
        });
      } else if (lower.includes("disturbed") || lower.includes("locked") || lower.includes("clone") || lower.includes("already used")) {
        // Hard-reset escape hatch: wipe every supabase-js localStorage key
        // (sb-*-auth-token etc.) and reload. With the no-op lock in place
        // this branch should be unreachable, but if a stale serialized
        // session is corrupt we'd still hit it — and the user shouldn't be
        // told "try again" indefinitely.
        try {
          Object.keys(localStorage)
            .filter((k) => k.startsWith("sb-") || k === "token" || k === "user")
            .forEach((k) => localStorage.removeItem(k));
        } catch (_) {}
        toast.error("Login state was stuck — clearing and reloading", { duration: 2500 });
        setTimeout(() => window.location.reload(), 1500);
      } else {
        toast.error(msg || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const brandName = companyInfo?.system_name || BRAND_NAME;
  const brandTagline = companyInfo?.system_subtitle || BRAND_TAGLINE;

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] bg-white">
      {/* LEFT: brand-navy hero panel with logo + editorial copy */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-brand-navy text-white p-12 xl:p-16">
        {/* Sky-blue bubbles in the background */}
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-brand-sky/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-32 w-[520px] h-[520px] rounded-full bg-brand-sky/20 blur-3xl pointer-events-none" />
        <div className="absolute top-[30%] right-12 w-3 h-3 rounded-full bg-brand-sky/70" />
        <div className="absolute top-[42%] right-32 w-2 h-2 rounded-full bg-brand-sky/50" />
        <div className="absolute bottom-[28%] right-24 w-4 h-4 rounded-full bg-brand-sky/60" />
        <div className="absolute top-[60%] left-20 w-2 h-2 rounded-full bg-brand-sky/40" />

        {/* Brand badge top-left */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="bg-white rounded-md p-1.5 shadow-md">
            <img src={LOGO} alt={brandName} className="w-9 h-9 object-contain" />
          </div>
          <div>
            <p className="font-display text-white text-lg font-bold tracking-tight">{brandName}</p>
            <p className="text-[11px] uppercase tracking-[0.18em] text-brand-sky font-medium">{brandTagline}</p>
          </div>
        </div>

        {/* Centered logo hero */}
        <div className="relative z-10 flex-1 flex items-center justify-center py-6">
          <img
            src={LOGO}
            alt={brandName}
            className="w-full max-w-md object-contain drop-shadow-[0_25px_50px_rgba(0,0,0,0.35)]"
          />
        </div>

        {/* Editorial copy bottom */}
        <div className="relative z-10 max-w-md">
          <p className="text-[11px] uppercase tracking-[0.2em] text-brand-sky font-bold mb-3">
            Operations Console — v2.0
          </p>
          <h2 className="font-display text-4xl xl:text-5xl font-extrabold text-white tracking-tight leading-[0.95]">
            Linen flow,<br />precisely managed.
          </h2>
        </div>
      </div>

      {/* RIGHT: stark login form */}
      <div className="flex flex-col justify-center px-8 sm:px-16 lg:px-20 py-12 bg-white fade-in">
        {/* Mobile brand */}
        <div className="lg:hidden flex items-center gap-3 mb-10">
          <div className="bg-brand-navy rounded-md p-1.5">
            <img src={LOGO} alt={brandName} className="w-9 h-9 object-contain bg-white rounded-sm" />
          </div>
          <div>
            <p className="font-display text-brand-navy text-lg font-bold tracking-tight">{brandName}</p>
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">{brandTagline}</p>
          </div>
        </div>

        <div className="max-w-sm">
          <p className="text-[11px] uppercase tracking-[0.2em] text-brand-sky font-bold mb-3">
            Sign in
          </p>
          <h1 data-testid="login-title" className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-brand-navy">
            Welcome back.
          </h1>
          <p data-testid="login-description" className="text-muted-foreground text-sm mt-3 leading-relaxed">
            Use your work email and password to access the operations console.
          </p>

          {/* Maintenance banner — surfaced to customers BEFORE they try to log in */}
          {maintenance.mode && (
            <div
              data-testid="maintenance-banner"
              className="mt-6 rounded-lg border-2 border-amber-400 bg-amber-50 p-4 flex items-start gap-3"
            >
              <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-display font-bold text-amber-900 mb-1">System maintenance in progress</p>
                <p className="text-amber-800 leading-relaxed">
                  {maintenance.message || "The system is temporarily unavailable. Hotel logins are paused while we update — please try again later."}
                </p>
                <p className="text-[11px] text-amber-700 mt-2 italic">
                  Operations staff (admin / manager / dispatcher) can still sign in.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleLogin} className="mt-10 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[11px] uppercase tracking-[0.16em] font-semibold text-brand-navy">
                Email
              </Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                placeholder="you@hotel.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 border-neutral-300 rounded-md focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-brand-navy focus-visible:shadow-[0_0_0_1px_hsl(var(--brand-navy))] text-base"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[11px] uppercase tracking-[0.16em] font-semibold text-brand-navy">
                Password
              </Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12 border-neutral-300 rounded-md focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-brand-navy focus-visible:shadow-[0_0_0_1px_hsl(var(--brand-navy))] text-base"
              />
            </div>

            <div className="flex items-start gap-2.5">
              <Checkbox
                id="terms-agree"
                data-testid="terms-checkbox"
                checked={agreed}
                onCheckedChange={(v) => setAgreed(!!v)}
                className="mt-0.5"
              />
              <label htmlFor="terms-agree" className="text-xs text-muted-foreground leading-relaxed select-none">
                I agree to the{" "}
                <button
                  type="button"
                  data-testid="terms-link"
                  onClick={() => setTermsOpen(true)}
                  className="text-brand-navy font-semibold underline underline-offset-2 hover:text-brand-navy-hover"
                >
                  Terms &amp; Conditions
                </button>
              </label>
            </div>

            <Button
              type="submit"
              data-testid="login-button"
              className="group w-full h-12 bg-brand-navy hover:bg-brand-navy-hover text-white rounded-md font-medium tracking-tight transition-colors disabled:opacity-60 flex items-center justify-center gap-2 shadow-sm"
              disabled={loading || !agreed}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Signing in
                </>
              ) : (
                <>
                  Sign in
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-10 pt-6 border-t border-border">
            <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground">
              Forgot password or need access? Contact your administrator.
            </p>
            {companyInfo?.phone && (
              <p className="mt-2 text-sm font-semibold text-brand-navy tracking-wide">
                <a href={`tel:${companyInfo.phone.replace(/\s+/g, "")}`} className="hover:underline" data-testid="login-admin-phone">
                  {companyInfo.phone}
                </a>
              </p>
            )}
          </div>
        </div>
      </div>

      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="terms-dialog">
          <DialogHeader>
            <DialogTitle className="text-brand-navy">Terms &amp; Conditions</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-neutral-700 whitespace-pre-wrap leading-relaxed" data-testid="terms-content">
            {terms || "No terms and conditions have been published yet."}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
